<template>
    <application-layout
        :property="property"
        :application="application"
        :experian_attempt="experian_attempt"
        :step_completion_status="$page.props.step_completion_status"
        :step_progress_data="$page.props.step_progress_data"
        :completed_steps_map="$page.props.completed_steps_map"
        sub-title=" - Dashboard"
    >
        <div class="bg-gray-50 rounded-lg shadow-md">
            <div class="p-6">
                <div class="grid sm:grid-cols-2 gap-4">
                    <div class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 hover:bg-blue-50">
                        <a :href="`https://www.google.com/maps?q=${property.full_address}`" target="_blank" class="block p-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-start">
                                    <font-awesome-icon :icon="faExternalLinkAlt" class="text-gray-400 mr-2 mt-1" size="sm" />
                                    <div>
                                        <h2 class="font-bold leading-tight text-blue-600 hover:text-blue-800">Property Address</h2>
                                        <h2 class="text-sm text-gray-600">
                                            {{ property.full_address }}
                                        </h2>
                                    </div>
                                </div>
                                <div>
                                    <font-awesome-icon :icon="faMapPin" size="2x" class="text-blue-500" />
                                </div>
                            </div>
                        </a>
                    </div>
                    <div class="bg-white p-4 rounded-lg shadow-md">
                        <h2 class="font-bold leading-tight">Agent/Landlord</h2>
                        <h4 class="text-sm">{{property.agent.name}} ({{property.agent.email}})</h4>
                    </div>
                    <div class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
                        <inertia-link :href="route('application.credit-report',application.id)" target="_blank" class="block p-4 hover:bg-blue-50">
                            <div class="flex items-center justify-between">
                                <div class="flex items-start">
                                    <font-awesome-icon :icon="faExternalLinkAlt" class="text-gray-400 mr-2 mt-1" size="sm" />
                                    <div>
                                        <h2 class="font-bold leading-tight text-blue-600 hover:text-blue-800">View My Report</h2>
                                        <h2 class="text-sm text-gray-600">
                                            Expires {{ application.expires_at }}
                                        </h2>
                                    </div>
                                </div>
                                <div>
                                    <font-awesome-icon :icon="faFileMagnifyingGlass" size="2x" class="text-blue-500" />
                                </div>
                            </div>
                        </inertia-link>
                    </div>
                    <div class="bg-white p-4 rounded-lg shadow-md">
                        <h2 class="font-bold leading-tight">Application Status</h2>
                        <h4 class="text-sm">
                            <span v-if="incompleteSteps.length === 0" class="text-green-600 font-medium">
                                Application Complete - Pending Review
                            </span>
                            <span v-else>
                                {{ application.status }}
                            </span>
                        </h4>
                    </div>
                </div>
                
                <!-- Congratulations Message - Shows when all steps are completed -->
                <div v-if="incompleteSteps.length === 0" class="mt-6 bg-green-50 rounded-md shadow-md border border-green-800 shadow-green-600">
                    <div class="p-6 white rounded-md">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <div class="ml-4">
                                <h3 class="text-lg font-medium text-green-800">Congratulations! You have completed your application process</h3>
                                <div class="mt-2 text-sm text-gray-900 space-y-2">
                                    <p>Thank you for completing your application{{ property.full_address ? ' for ' + property.full_address : '' }}.</p>
                                    <p>The property owner or listing agent will review your application and be in touch with you soon.</p>
                                    <div class="mt-3 p-3 border border-green-600 rounded-md">
                                        <p class="font-bold text-green-800 mb-1">What happens next?</p>
                                        <ul class="list-disc list-inside space-y-1 text-gray-900">
                                            <li>Your agent/landlord has been notified</li>
                                            <li>They will review your application and report</li>
                                            <li class="font-bold">They may request you to provide enhanced screening steps</li>
                                            <li>You'll be contacted directly with their decision</li>
                                        </ul>
                                    </div>
                                    <p class="mt-2 text-sm italic">Please do not contact RentRisk about your application status. Contact {{ property.agent ? property.agent.name : 'your agent/landlord' }} directly for updates.</p>
                                </div>
                                <div class="mt-4 flex items-center space-x-3">
                                    <inertia-link 
                                        :href="route('application.credit-report', application.id)" 
                                        target="_blank"
                                        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-700 hover:bg-green-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                        View My Report
                                        <svg class="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                        </svg>
                                    </inertia-link>
                                    <button 
                                        type="button"
                                        class="inline-flex items-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                        @click="celebrateAgain"
                                    >
                                        <!-- <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg> -->
                                        <font-awesome-icon :icon="faPartyHorn" class="pr-2" />
                                        Celebrate!
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Remaining Steps - Only show if there are incomplete steps -->
                <div v-if="incompleteSteps.length > 0" class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
                    <div class="grid grid-cols-1 gap-4 p-6 bg-gray-50">
                        <card-header-footer
                            v-for="(step, index) in incompleteSteps"
                            :key="index"
                            :step_name="step.step_name"
                            :header="step.display_name"
                            :body="step.description"
                            :icon="step.icon"
                            :url="step.url"
                            :is_locked="step.is_locked"
                            :status="step.is_locked ? 'Locked' : 'Ready to Start'"
                            :application="application"
                            :addon_id="step.addon_id"
                            :step_number="step.step_number"
                            :is_next_step="index === 0 && !step.is_locked"
                            footer="For more information, contact support."
                        />
                    </div>
                </div>

                <div v-if="completedSteps.length > 0" class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
                    <div class="grid grid-cols-1 gap-4 bg-gray-50 p-6">
                        <card-header-footer
                            v-for="(step, index) in completedSteps"
                            :key="index"
                            :step_name="step.step_name"
                            :header="step.display_name"
                            :body="step.description"
                            :icon="step.icon"
                            :url="step.url"
                            :status="'Completed'"
                            :application="application"
                            :addon_id="step.addon_id"
                            :step_number="step.step_number"
                            :is_next_step="false"
                            footer="For more information, contact support."
                        />
                    </div>
                </div>
            </div>
        </div>
    </application-layout>
</template>

<script>
import axios from "axios";
import { router } from '@inertiajs/vue3';
import CardHeaderFooter from '../../Shared/CardHeaderFooter.vue';
import ApplicationLayout from "./ApplicationLayout.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faMapPin, faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons";
import { faFileMagnifyingGlass, faPartyHorn } from "@fortawesome/pro-light-svg-icons";
import { celebrate, loadConfetti } from "@/Shared/confetti";

export default {
    components: {
        CardHeaderFooter,
        ApplicationLayout,
        FontAwesomeIcon
    },
    props: {
        property: Object,
        application: Object,
        experian_attempt: Object,
        step_completion_status: Object,
        step_progress_data: Object,
        completed_steps_map: Object,
    },
    data() {
        return {
            faMapPin,
            faFileMagnifyingGlass,
            faExternalLinkAlt,
            faPartyHorn,
            pageSubTitle: '- Dashboard',
            addon_actions: [],
        }
    },
    computed: {
        incompleteSteps() {
            if (!this.step_completion_status) return [];
            return Object.values(this.step_completion_status).filter(step => !step.is_completed);
        },
        completedSteps() {
            if (!this.step_completion_status) return [];
            return Object.values(this.step_completion_status).filter(step => step.is_completed);
        }
    },

    mounted() {
        // Check if all steps are completed and trigger confetti
        if (this.incompleteSteps.length === 0 && !sessionStorage.getItem(`confetti_shown_${this.application.id}`)) {
            loadConfetti().then(() => {
                setTimeout(() => {
                    celebrate();
                    sessionStorage.setItem(`confetti_shown_${this.application.id}`, 'true');
                }, 500);
            });
        }
    },

    methods: {
        celebrateAgain() {
            loadConfetti().then(() => {
                celebrate();
            });
        },
    }
}
</script>